/* eslint-disable prettier/prettier */
/* eslint-disable react/no-unescaped-entities */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { AppContext } from 'contexts';
import {
  makeStyles,
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  Grid,
} from '@material-ui/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { KEYS } from 'config/constants';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { startsWith } from 'lodash';

const useStyles = makeStyles((theme) => {
  return {
    login: {
      width: 300,
      position: 'relative',
      top: '20%',
      marginLeft: 'auto',
      marginRight: 'auto',
      alignContent: 'center',
      backgroundColor: 'white',
      padding: 30,
      animation: 'slide-down .2s ease-in',

      '@media (max-width: 767px)': {
        top: 101,
      },
    },
    row: {
      height: '100%',
      marginBottom: 20,
      flexDirection: 'row',
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      marginLeft: 5,
    },
    backIcon: {
      cursor: 'pointer',
    },
    heading: {
      textAlign: 'center',
      margin: theme.spacing(1, 0, 4),
    },
    submitButton: {
      marginTop: theme.spacing(4),
    },
    textButton: {
      fontSize: '12px',
      marginBottom: 5,
    },
    button: {
      fontSize: '12px',
      color: 'white',
      marginTop: 5,
      marginBottom: 5,
    },
    verifyButton: {
      marginTop: 10,
    },
    resendMessage: {
      flexDirection: 'row',
    },
    warning: {
      fontSize: 12,
      color: 'grey',
    },
    error: {
      color: 'red',
      fontSize: 12,
    },
  };
});

function Login() {
  const classes = useStyles();
  const appContext = useContext(AppContext);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [timer, setTimer] = useState(60);
  const [isVerified, setIsVerified] = useState(false);
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    captchaError: false,
    email: false,
    phoneNumber: false,
  });
  const {
    loginModalOpen,
    closeLoginModal,
    sendVerificationCode,
    verifyPhoneNumber,
    verifyRequest,
    resendTimer,
    resendDisabled,
    setResendDisabled,
    sentVerificationCode,
  } = appContext;
  const recaptchaRef = useRef(null);
  useEffect(() => {
    setTimer(resendTimer);
    if (resendTimer) {
      setResendDisabled(true);
    }
    setTimeout(() => {
      setResendDisabled(false);
    }, resendTimer * 1000);
  }, [resendTimer]);
  useEffect(() => {
    const myInterval = setInterval(() => {
      setTimer(timer - 1);
      if (timer === 0) {
        clearInterval(myInterval);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  });
  const validateEmail = (input) => {
    let isValid;
    if (email) {
      const re = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      isValid = re.test(String(email).toLowerCase());
      if (!isValid) {
        setErrors({ ...errors, email: true });
      } else {
        setErrors({ ...errors, email: false });
      }
    } else {
      setErrors({ ...errors, email: false });
      isValid = true;
    }
    return isValid;
  };
  const validatePhoneNumberLength = () => {
    if (phoneNumber !== null && phoneNumber.length < 12) {
      setErrors({ ...errors, phoneNumber: true });
      return false;
    }
    setErrors({ ...errors, phoneNumber: false });
    return true;
  };
  const handleValidate = (e) => {
    e.preventDefault();
    if (!firstName) {
      setErrors({ ...errors, firstName: true });
    }
    if (!lastName) {
      setErrors({ ...errors, lastName: true });
    }
    if (!validateEmail(email)) {
      setErrors({ ...errors, email: true });
    }
    if (phoneNumber !== null && phoneNumber.length < 12) {
      setErrors({ ...errors, phoneNumber: true });
    }
    if (
      firstName &&
      lastName &&
      (phoneNumber || email) &&
      validateEmail(email) &&
      validatePhoneNumberLength()
    ) {
      setShowCodeInput(true);
    }
    return false;
  };
  const onCaptchaChange = (value) => {
    setIsVerified(true);
    setErrors({ ...errors, captchaError: false });
  };
  const onCaptchaExpired = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const onCaptchaErrored = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const validatePhoneNumber = (inputNumber, country, countries) => {
    return countries.some(() => {
      return (
        startsWith(inputNumber, country.dialCode) ||
        startsWith(country.dialCode, inputNumber)
      );
    });
  };
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };
  const entryView = () => {
    return (
      <Modal
        open={loginModalOpen}
        onClose={closeLoginModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={classes.login}>
          <Typography variant='h5'>Vérification</Typography>
          <form>
            <TextField
              variant='outlined'
              value={firstName}
              onChange={(e) => {
                return setFirstName(e.target.value);
              }}
              margin='normal'
              label='Prénom'
              fullWidth
              size='small'
              required
              error={errors.firstName}
            />
            <TextField
              variant='outlined'
              value={lastName}
              onChange={(e) => {
                return setLastName(e.target.value);
              }}
              margin='normal'
              label='Nom de famille'
              fullWidth
              size='small'
              required
              error={errors.lastName}
            />
            <PhoneInput
              country='ca'
              preferredCountries={['ca', 'us']}
              enableSearch
              value={phoneNumber}
              inputProps={{
                name: 'Numéro de téléphone',
                required: true,
              }}
              prefix='+'
              countryCodeEditable={false}
              onChange={(phone) => {
                return setPhoneNumber(`+${phone}`);
              }}
              onBlur={(e) => {
                if (phoneNumber !== null) {
                  return validatePhoneNumberLength();
                }
                setErrors({ ...errors, phoneNumber: false });
                return null;
              }}
              isValid={(inputNumber, country: any, countries) => {
                return validatePhoneNumber(inputNumber, country, countries);
              }}
              defaultErrorMessage='Numéro de téléphone invalide'
              containerStyle={{ marginBottom: 5, marginTop: 15 }}
              inputStyle={{ margin: 0, width: '100%', height: 40 }}
            />
            {phoneNumber !== null &&
              phoneNumber.length > 2 &&
              phoneNumber.length < 12 &&
              errors.phoneNumber && (
                <Typography className={classes.error}>
                  Numéro de téléphone invalide
                </Typography>
            )}
            <TextField
              variant='outlined'
              value={email}
              onChange={(e) => {
                return handleEmailChange(e);
              }}
              onBlur={(e) => {
                return validateEmail(e.target.value);
              }}
              error={errors.email}
              helperText={errors.email ? 'Email invalide' : ''}
              margin='normal'
              label='Email'
              fullWidth
              size='small'
            />
            <Button
              type='button'
              fullWidth
              variant='outlined'
              color='primary'
              className={classes.verifyButton}
              disabled={!firstName || !lastName || (!phoneNumber && !email)}
              onClick={(e) => {
                return handleValidate(e);
              }}
            >
              Vérifier
            </Button>
          </form>
        </Box>
      </Modal>
    );
  };
  const resendCodeView = () => {
    return (
      <Modal
        open={loginModalOpen}
        onClose={closeLoginModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={classes.login}>
          <ArrowBackIcon
            onClick={() => {
              setShowCodeInput(false);
            }}
          />
          <Typography variant='h5'>OTP Vérification</Typography>
          <Typography variant='subtitle2'>
            {`Entrer le OTP envoyé à ${phoneNumber}`}
          </Typography>
          <div>
            <TextField
              variant='outlined'
              value={verificationCode}
              onChange={(e) => {
                return setVerificationCode(e.target.value);
              }}
              margin='normal'
              label='Le code de vérification'
              fullWidth
              size='small'
            />
            <Grid container className={classes.resendMessage}>
              <Grid item xs={8}>
                <Typography variant='subtitle2'>N'a pas reçu le OTP?</Typography>
              </Grid>
              <Grid item xs={4}>
                <Button
                  size='small'
                  onClick={() => {
                    return sendVerificationCode(firstName, lastName, phoneNumber, email);
                  }}
                  fullWidth
                  variant='text'
                  color='primary'
                  disabled={resendDisabled}
                  className={classes.textButton}
                >
                  Renvoyer
                </Button>
              </Grid>
            </Grid>
            {resendDisabled ? (
              <Typography className={classes.warning}>
                {`Veuillez attendre ${timer} secondes pour renvoyer le nouveau code`}
              </Typography>
            ) : null}
            <Button
              className={classes.button}
              fullWidth
              variant='contained'
              color='primary'
              disabled={verificationCode.length !== 6}
              onClick={() => {
                return verifyPhoneNumber(phoneNumber, verificationCode);
              }}
            >
              Vérifier & Appel le courtier
            </Button>
          </div>
        </Box>
      </Modal>
    );
  };
  const recaptchaView = () => {
    return (
      <Modal
        open={loginModalOpen}
        onClose={closeLoginModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={classes.login}>
          <Box className={classes.row}>
            <ArrowBackIcon
              className={classes.backIcon}
              onClick={() => {
                setShowCodeInput(false);
              }}
            />
            <Typography className={classes.title} variant='h5'>
              Vérification
            </Typography>
          </Box>
          <Box className={classes.row}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={KEYS.CAPTCHA_KEY}
              onChange={onCaptchaChange}
              onExpired={onCaptchaExpired}
              onErrored={onCaptchaErrored}
            />
          </Box>
          <Button
            className={classes.button}
            fullWidth
            variant='contained'
            color='primary'
            disabled={!isVerified}
            onClick={() => {
              return verifyRequest(firstName, lastName, phoneNumber, email);
            }}
          >
            Vérifier & Appel le courtier
          </Button>
        </Box>
      </Modal>
    );
  };

  return <>{showCodeInput ? recaptchaView() : entryView()}</>;
}

export default Login;
